/**
 * What a metric shows when it is absent.
 *
 * Absent is not zero. A platform that does not report viewability, and a project whose
 * campaigns report no spend, both have nothing to say — and printing 0 would state a
 * measurement nobody made. This codebase has already shipped that conflation twice.
 */
export const EM_DASH = '—';

export type MetricFormat = 'integer' | 'currency' | 'percent' | 'decimal';

/**
 * Whether `Intl` will accept this as a currency at all.
 *
 * It demands exactly three ASCII letters and throws a `RangeError` on anything else, which
 * inside a render takes the whole dashboard down rather than one figure. The stored value is
 * whatever the platform reported and the column only caps its length, so `US` reaches here
 * as readily as `USD`. An unknown-but-well-formed code like `XYZ` is fine — `Intl` prints it
 * beside the number, which is the same thing this does deliberately for the ones it knows.
 */
const CURRENCY_CODE = /^[A-Za-z]{3}$/;

export function formatMetric(
  value: number | null,
  format: MetricFormat,
  currency?: string | null,
): string {
  if (value === null) return EM_DASH;

  switch (format) {
    case 'integer':
      return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value);
    case 'currency':
      // No currency means no symbol. Defaulting to '$' would invent one.
      //
      // The code follows the symbol because symbols are not unique: $ is used by a dozen
      // currencies and £ by several, so a figure carrying only a symbol is ambiguous in
      // exactly the projects that run in more than one.
      // Uppercased for the suffix only: `Intl` normalises the code it prints, so a stored
      // `usd` would otherwise read as "$1,234.50 (usd)" beside its own uppercased twin.
      return currency && CURRENCY_CODE.test(currency)
        ? `${new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(value)} (${currency.toUpperCase()})`
        : new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(value);
    case 'percent':
      return `${(value * 100).toFixed(2)}%`;
    case 'decimal':
      return value.toFixed(2);
  }
}
